import { zodResolver } from '@hookform/resolvers/zod';
import { getCustomerConfig } from '@pelicargo/tenants';
import {
  AircraftRestriction,
  AircraftType,
  BatteryType,
  BatteryTypeVariant,
  CargoCooling,
  CargoCoolingRange,
  CargoItem,
  CargoType,
  CargoTypeSub,
  DimensionUnit,
  FreightService,
  FreightServiceAddOn,
  HandlingOptions,
  PackingGroup,
  PackingInstruction,
  RequestFormValues,
  Section,
  ServiceType,
  UnNumber,
  WeightUnit,
} from '@pelicargo/types';
import { useError, useSuccess } from '@pelicargo/ui';
import { Buffer } from 'buffer';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSessionStorage } from 'usehooks-ts';
import { z } from 'zod';

import { TrpcInputs } from '../../config/trpc';
import { documentSchema } from '../../utils/schemas';

export type RequestForm = Partial<NonNullable<TrpcInputs['createRequest']>>;

type UseRequestForm = { methods: UseFormReturn<RequestFormValues> };

const validationSchema = z
  .object({
    origin_airports: z
      .array(z.string(), {
        required_error: 'At least one origin airport is required',
      })
      .min(1, 'At least one origin airport is required')
      .max(4),
    destination_airports: z
      .array(z.string(), {
        required_error: 'At least one destination airport is required',
      })
      .min(1, 'At least one destination airport is required')
      .max(4),
    cargo_origin: z
      .object({
        recipient: z.string().nullish(),
        line_one: z.string().nullish(),
        line_two: z.string().nullish(),
        city: z.string().nullish(),
        state: z.string().nullish(),
        zip_code: z.string().nullish(),
        country: z.string().nullish(),
        name: z.string().nullish(),
        latitude: z.number().nullish(),
        longitude: z.number().nullish(),
        place_id: z.string().nullish(),
      })
      .nullish(),
    dropoff_by: z.coerce.date().nullish(),
    cargo_type: z.nativeEnum(CargoType),
    cargo_type_sub: z.nativeEnum(CargoTypeSub).nullish(),
    is_known_shipper: z.boolean({
      required_error: 'Please select whether the shipper is a TSA Known Shipper',
      invalid_type_error: 'Please select whether the shipper is a TSA Known Shipper',
    }),
    commodity: z.string().nullish(),
    is_consolidated: z.boolean().default(false),

    cargo_details: z.string(),
    original_dimension_unit: z.nativeEnum(DimensionUnit),
    original_weight_unit: z.nativeEnum(WeightUnit),
    gross_weight: z.coerce.number().nullish(),
    total_pieces: z.number().optional(),
    volumetric_weight: z.number().optional(),

    is_llm_generated: z.boolean().optional(),
    // Carries the AI run id from the importer through to createRequest so the
    // created Request links back to its AgentExecution and the correction is recorded.
    agent_execution_id: z.number().optional(),

    freight_service: z.nativeEnum(FreightService).optional(),
    extra_freight_services: z.array(z.nativeEnum(FreightServiceAddOn)).optional(),

    cargo_cooling_range: z.string().nullish(),
    cargo_cooling: z.string().nullish(),

    aircraft_types: z.array(z.string()).optional(),

    cargo: z
      .array(
        z.object({
          quantity: z.number().min(1, 'This field is required'),
          length: z.coerce.number().min(1, 'Must be at least 1'),
          width: z.coerce.number().min(1, 'Must be at least 1'),
          height: z.coerce.number().min(1, 'Must be at least 1'),
          weight: z.coerce.number().optional(), // same as L,W, and H but has extra validation in superRefine
          handling: z.nativeEnum(HandlingOptions).optional(),
          packing: z.array(z.string()).optional(),
        }),
      )
      .min(1, 'You must have at least one piece of cargo'),

    un_number: z.coerce.string().nullish(),
    class: z.string().nullish(),
    packing_group: z.union([z.nativeEnum(PackingGroup), z.literal('')]).nullish(), // Accounts for EmptyString type
    aircraft_restriction: z.string().nullish(),
    packing_instruction: z.string().nullish(),
    section: z.string().nullish(),
    battery_type: z.string().nullish(),
    battery_type_variant: z.string().nullish(),
    documents: z
      .array(
        documentSchema.extend({
          id: z.coerce.number().nullish(),
          created_at: z.coerce.date().nullish(),
          updated_at: z.coerce.date().nullish(),
          document_id: z.coerce.number().nullish(),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.cargo_type === CargoType.Perishable) {
      if (!Object.values(CargoTypeSub).includes(data.cargo_type_sub as CargoTypeSub)) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_enum_value,
          options: Object.values(CargoTypeSub),
          received: data.cargo_type_sub ?? '',
          message: 'Invalid Sub Type',
          path: ['cargo_type_sub'],
        });
      }

      if (!Object.values(CargoCoolingRange).includes(data.cargo_cooling_range as CargoCoolingRange)) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_enum_value,
          options: Object.values(CargoCoolingRange),
          received: data.cargo_cooling_range ?? '',
          message: 'Invalid Temperature Range',
          path: ['cargo_cooling_range'],
        });
      }

      if (!Object.values(CargoCooling).includes(data.cargo_cooling as CargoCooling)) {
        ctx.addIssue({
          code: z.ZodIssueCode.invalid_enum_value,
          options: Object.values(CargoCooling),
          received: data.cargo_cooling ?? '',
          message: 'Cooling Type Required',
          path: ['cargo_cooling'],
        });
      }
    }

    if (data.cargo_details === 'Weight Per Piece') {
      const cargoList = data.cargo || [];
      for (const [index, cargo] of cargoList.entries()) {
        if (Number(cargo.weight) < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.too_small,
            type: 'number',
            minimum: 1,
            inclusive: true,
            message: 'Must be at least 1',
            path: ['cargo', index, 'weight'],
          });
        }
      }
    }

    const { cargo_type, un_number, battery_type } = data;

    if (cargo_type === CargoType.DangerousGoods) {
      if (!!un_number && !/^\d{4}$/.test(un_number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'UN Number too short',
          path: ['un_number'],
        });
      }

      if (!!data.class && !/^\d{1,2}(\.\d{1,2})?$/.test(data.class ?? '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid Class',
          path: ['class'],
        });
      }

      if (!!data.packing_group && !Object.values(PackingGroup).includes(data.packing_group as PackingGroup)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Group Required',
          path: ['packing_group'],
        });
      }
    }

    if (battery_type === BatteryType.DG_BATTERIES_IN_EQUIPMENT) {
      if (!un_number || !Object.values(UnNumber).includes(un_number as UnNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'UN Number Required',
          path: ['un_number'],
        });
      }

      if (!Object.values(Section).includes(data.section as Section)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Section Required',
          path: ['section'],
        });
      }

      if (!Object.values(PackingInstruction).includes(data.packing_instruction as PackingInstruction)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Instruction Required',
          path: ['packing_instruction'],
        });
      }
    }

    if (cargo_type === CargoType.DangerousGoods || battery_type === BatteryType.DG_BATTERIES_IN_EQUIPMENT) {
      if (!Object.values(AircraftRestriction).includes(data.aircraft_restriction as AircraftRestriction)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Aircraft Variant Required',
          path: ['aircraft_restriction'],
        });
      }
    }

    if (cargo_type === CargoType.Batteries && !Object.values(BatteryType).includes(battery_type as BatteryType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Battery Type Required',
        path: ['battery_type'],
      });
    }

    if (
      battery_type === BatteryType.NON_RESTRICTED_BATTERIES_IN_EQUIPMENT &&
      !Object.values(BatteryTypeVariant).includes(data.battery_type_variant as BatteryTypeVariant)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Battery Type Variant Required',
        path: ['battery_type_variant'],
      });
    }
  });

export const defaultCargoItem: Partial<CargoItem> = {
  quantity: 1,
  handling: HandlingOptions.Stackable,
  packing: [],
};

export const defaultRequestFormValues: RequestFormValues = {
  origin_airports: [],
  destination_airports: [],
  dropoff_by: null,
  commodity: null,
  original_dimension_unit: DimensionUnit.in,
  original_weight_unit: WeightUnit.kg,
  is_known_shipper: null,
  is_pre_screened: null,

  cargo_origin: {},
  cargo_details: 'Weight Per Piece',
  cargo_type: CargoType.GeneralCargo,
  cargo_type_sub: null,
  cargo_cooling_range: null,
  cargo_cooling: null,

  aircraft_types: Object.values(AircraftType),
  cargo: [defaultCargoItem as any],

  total_pieces: 0,
  volumetric_weight: 0,
  gross_weight: 0,

  flight: 'Any',
  service_type: ServiceType.General,
  freight_service: FreightService.AFR,
  extra_freight_services: [],
  is_consolidated: true,

  un_number: null,
  class: null,
  packing_group: null,
  aircraft_restriction: null,
  packing_instruction: null,
  section: null,
  battery_type: null,
  battery_type_variant: null,
  documents: [],
  default_contacts: [],

  ...(getCustomerConfig()?.requestFormDefaults || {}),
};

export const useRequestForm = (): UseRequestForm => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();

  const [request, setRequest] = useSessionStorage<RequestFormValues>('request', defaultRequestFormValues);

  useEffect(() => {
    if (isEmpty(request) || !validationSchema.safeParse(request)?.success) {
      setRequest(defaultRequestFormValues);
    } else reset(request);
    // should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (document.location.hash.startsWith('#prefill:')) {
      const decodedRequest = Buffer.from(document.location.hash.slice(9), 'base64').toString('utf-8');
      const incomingRequest = JSON.parse(decodedRequest);

      // Check to see if either version exists
      const rawDropoffBy = incomingRequest?.drop_off_by ?? incomingRequest?.dropoff_by;
      const dropoff_by = rawDropoffBy ? new Date(rawDropoffBy) : null;

      const modifiedRequest = {
        ...(incomingRequest || {}),
        dropoff_by: isNaN(dropoff_by?.getTime()) ? undefined : dropoff_by,
        cargo:
          incomingRequest?.cargo?.map((cargo) => {
            let handling = cargo?.handling?.[0] as HandlingOptions;
            // Check to see if incomingHandling is typeof HandlingOptions
            if (!HandlingOptions?.[handling]) {
              handling = HandlingOptions.Stackable;
            }
            return {
              // Zero is an okay fallback, as it will get caught in Zod's filtering
              ...(cargo || {}),
              weight: Number(cargo?.weight) || 0,
              height: Number(cargo?.height) || 0,
              width: Number(cargo?.width) || 0,
              length: Number(cargo?.length) || 0,
              quantity: Number(cargo?.quantity) || 0,
              handling,
            };
          }) || [],
        // This should minimize data discrepancies
        destination_airports: incomingRequest?.destination_airports?.filter((airport) => airport?.length === 3),
        origin_airports: incomingRequest?.origin_airports?.filter((airport) => airport?.length === 3),
      };

      // We can't account for every error, this causes it to fail gracefully.
      try {
        setRequest(modifiedRequest);

        // Since we need to reload, we can lie and say that it's importing. Lets people know that it's all good
        handleSuccess('Importing request...');
        setTimeout(() => window.location.reload(), 3000);
      } catch (error) {
        console.log(error);
        handleError(
          'Unable to auto-fill request. The Customer Success team has been notified and will be in touch with you soon.',
        );
      }
      document.location.hash = '';
    }
  });

  const { reset, getValues, setValue, ...methods } = useForm<RequestFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
    defaultValues: defaultRequestFormValues,
  });

  return { methods: { ...methods, getValues, setValue, reset } };
};
